// src/components/video/storyboards/index.js
//
// Storyboard TEMPLATES. A template is a starting point for the Storyboard
// editor in the Lesson video studio: it guesses the scene for each lyric line
// by the line's opening words. Whatever you change in the editor is saved to
// Firestore (lessonStoryboards/{slug}) for that topic and used from then on.

import tellingTime from "./tellingTime";

export const STORYBOARDS = [{ key: "tellingTime", storyboard: tellingTime }];

export const templateByKey = (key) => STORYBOARDS.find((s) => s.key === key)?.storyboard || null;

const squash = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

/** True when this Topic Library topic looks like the template's song. */
export const storyboardFitsTopic = (storyboard, topic) => {
  const keys = (storyboard.topics || []).map(squash);
  return (
    keys.includes(squash(topic.slug)) ||
    keys.includes(squash(topic.id)) ||
    squash(topic.title) === squash(storyboard.title)
  );
};

/** The first template that fits this topic, or null. */
export const templateForTopic = (topic) =>
  STORYBOARDS.find((s) => storyboardFitsTopic(s.storyboard, topic)) || null;

/* ------------------------------------------------------------------ */
/* Lyric lines                                                         */
/* ------------------------------------------------------------------ */

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[‘’`]/g, "'")
    .replace(/[^a-z0-9' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const HEADER_RE = /^\s*[([]\s*([^)\]]{1,40}?)\s*[)\]]\s*:?\s*$/;
const CREDIT_RE = /^\s*(©|\(c\)\s|copyright\b)/i;

/**
 * The lines a storyboard can give a scene to: every sung line, with its
 * index, text and section label ("Chorus", "Verse 2").
 */
export const sungLinesOf = (lines) => {
  const out = [];
  let section = "";
  (lines || []).forEach((raw, i) => {
    const s = String(raw || "").trim();
    if (!s) return;
    const h = s.match(HEADER_RE);
    if (h) {
      section = h[1].replace(/^\w/, (c) => c.toUpperCase());
      return;
    }
    if (CREDIT_RE.test(s)) return;
    out.push({ i, text: s, section });
  });
  return out;
};

/* ------------------------------------------------------------------ */
/* Template → editable storyboard                                      */
/* ------------------------------------------------------------------ */

// JSON-safe copy (Firestore refuses undefined and functions).
const clean = (v) => JSON.parse(JSON.stringify(v ?? null));

/**
 * Builds an editable storyboard for THIS song's lyrics from a template.
 * Each sung line gets the template shot whose opening words it starts with
 * (respecting "last chorus" rules); lines with no match get { keep: true },
 * which shows as "same scene continues" in the editor.
 *
 * @returns {{ title, template, intro, outro, byLine, matched, total }}
 */
export const seedFromTemplate = (template, lines, title) => {
  const byLine = {};
  const seen = {};
  let matched = 0;
  const sung = sungLinesOf(lines);
  sung.forEach(({ i, text }) => {
    const n = norm(text);
    seen[n] = (seen[n] || 0) + 1;
    const occ = seen[n];
    const shot = template
      ? (template.shots || []).find(
          (s) =>
            n.startsWith(norm(s.line)) &&
            (!s.minOccurrence || occ >= s.minOccurrence) &&
            (!s.occurrence || occ === s.occurrence)
        )
      : null;
    if (shot) {
      const { line, id, minOccurrence, occurrence, ...rest } = shot;
      byLine[i] = clean(rest);
      matched += 1;
    } else {
      byLine[i] = { keep: true };
    }
  });
  return {
    title: title || template?.title || "",
    template: template ? STORYBOARDS.find((s) => s.storyboard === template)?.key || "" : "",
    intro: clean(template?.intro || { walkIn: true, scene: "students", cast: {} }),
    outro: clean(template?.outro || { after: 6, banner: "Great job!", cast: {} }),
    byLine,
    matched,
    total: sung.length,
  };
};

/** A blank storyboard: students on stage, every line "same scene". */
export const blankStoryboard = (lines, title) => seedFromTemplate(null, lines, title);

export default STORYBOARDS;
